import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

const C = {
  purple: "#5660FB",
  purpleDeep: "#4C56E0",
  purpleSoft: "#F5F6FE",
  purpleLight: "#ECEEF9",
  teal: "#34C9A2",
  tealBg: "#E8FAF4",
  tealPale: "#F5FBF9",
  tealBright: "#4CE0AF",
  amber: "#F5A623",
  amberBg: "#FFF8EC",
  red: "#E05656",
  redBg: "#FDE8E8",
  dark: "#2D3047",
  body: "#5A5E78",
  muted: "#8B8FA3",
  mutedLight: "#B0B4C5",
  border: "#ECEDF2",
  borderLight: "#F0F1F5",
  bg: "#FAFBFC",
  bgCard: "#FFFFFF",
  bgInput: "#F5F6F8",
}

const FONT_MONO = "'IBM Plex Mono', monospace"

const METRICS = {
  received: {
    label: "Received",
    description: "Percent of submitted apps the hospital has confirmed receiving",
    goal: 90,
    deadline: 14,
    deadlineLabel: "2 weeks",
    goalCopy: "Goal: 90% within 2 weeks of sending",
    columns: [0, 3, 7, 10, 14, 21, 28],
  },
  completed: {
    label: "Completed",
    description: "Percent of submitted apps with a final determination from the hospital",
    goal: 85,
    deadline: 60,
    deadlineLabel: "8 weeks",
    goalCopy: "Goal: 85% within 8 weeks of submission",
    columns: [0, 7, 14, 28, 42, 56, 60, 75],
  },
  approved: {
    label: "Approved",
    description: "Approval rate among completed apps (approved / (approved + denied))",
    goal: 85,
    deadline: 90,
    deadlineLabel: "terminal",
    goalCopy: "Goal: 85%+ approval rate among completed apps",
    columns: [0, 14, 28, 42, 56, 70, 84, 90],
  },
}

function hexToRgb(h) {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]
}

function lerpColor(a, b, t) {
  const [ar, ag, ab] = hexToRgb(a)
  const [br, bg, bb] = hexToRgb(b)
  return `rgb(${Math.round(ar + (br - ar) * t)}, ${Math.round(ag + (bg - ag) * t)}, ${Math.round(ab + (bb - ab) * t)})`
}

function cellBg(value, goal) {
  if (value == null) return "transparent"
  const t = Math.min(1.15, value / goal)
  if (t <= 1) return lerpColor(C.tealPale, C.teal, t)
  return lerpColor(C.teal, C.tealBright, (t - 1) / 0.15)
}

function cellTextColor(value, goal) {
  if (value == null) return C.mutedLight
  const t = Math.min(1, value / goal)
  return t > 0.55 ? "#0F4938" : C.dark
}

function cohortLineColor(idx, total) {
  const t = total === 1 ? 0 : idx / (total - 1)
  return lerpColor(C.purple, "#C4C9FF", t)
}

function parseRgb(color) {
  if (color.startsWith("#")) return hexToRgb(color)
  const m = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
  if (m) return [parseInt(m[1]), parseInt(m[2]), parseInt(m[3])]
  return [0, 0, 0]
}

function fadeColor(color, opacity) {
  const [r, g, b] = parseRgb(color)
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export default class extends Controller {
  static values = {
    cohorts: Object,
    dimensions: Object,
    hasMore: Object,
    cohortsUrl: String,
  }

  static targets = [
    "granularityBtn",
    "metricBtn",
    "dimensionBtn",
    "timeframeBtn",
    "timeframeControls",
    "locationControls",
    "locationFilter",
    "locationFilterLabel",
    "locationPanel",
    "locationSearch",
    "locationOptions",
    "loadMoreControls",
    "loadMoreBtn",
    "loadMoreLabel",
    "tableBody",
    "tableHead",
    "chartContainer",
    "chartLegend",
    "sectionDescription",
    "goalCopy",
    "cohortCount",
    "chartNote",
  ]

  static LOAD_MORE_MONTHS = 3
  static INITIAL_MONTHS_BACK = { weekly: 4, monthly: 9 }

  connect() {
    this.granularity = "weekly"
    this.metric = "completed"
    this.dimension = "all"
    this.timeframe = "all"
    this.hoveredCohort = null
    this.chart = null
    // Empty set means "all locations" (the default).
    this.selectedLocations = new Set()
    this.locationPanelOpen = false

    // Cohort retention data for the "All" dimension. `defaultCohorts` is the
    // unfiltered, initial-window payload embedded on page load; `cohortData`
    // holds whatever is currently displayed (possibly location-filtered or
    // extended further back via "Load more"). `monthsBack` tracks the loaded
    // window per granularity.
    this.defaultCohorts = this.cohortsValue || {}
    this.cohortData = {
      weekly: (this.defaultCohorts.weekly || []).slice(),
      monthly: (this.defaultCohorts.monthly || []).slice(),
    }
    this.monthsBack = { ...this.constructor.INITIAL_MONTHS_BACK }
    const initialHasMore = this.hasMoreValue || {}
    this.hasMore = { weekly: !!initialHasMore.weekly, monthly: !!initialHasMore.monthly }
    this.loadingCohorts = false

    this.onDocumentClick = (event) => {
      if (!this.locationPanelOpen) return
      if (this.hasLocationFilterTarget && !this.locationFilterTarget.contains(event.target)) {
        this.closeLocationPanel()
      }
    }
    document.addEventListener("click", this.onDocumentClick)
    this.render()
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  setGranularity(event) {
    const val = event.currentTarget.dataset.value
    if (val === this.granularity) return
    this.granularity = val
    this.hoveredCohort = null
    this.updateToggleButtons("granularity")
    this.refreshCohorts()
  }

  setMetric(event) {
    const val = event.currentTarget.dataset.value
    if (val === this.metric) return
    this.metric = val
    this.hoveredCohort = null
    this.updateToggleButtons("metric")
    this.render()
  }

  setDimension(event) {
    const val = event.currentTarget.dataset.value
    if (val === this.dimension) return
    this.dimension = val
    this.hoveredCohort = null
    this.updateToggleButtons("dimension")
    this.render()
  }

  setTimeframe(event) {
    const val = event.currentTarget.dataset.value
    if (val === this.timeframe) return
    this.timeframe = val
    this.hoveredCohort = null
    this.updateToggleButtons("timeframe")
    this.render()
  }

  updateToggleButtons(group) {
    const targetMap = { granularity: this.granularityBtnTargets, metric: this.metricBtnTargets, dimension: this.dimensionBtnTargets, timeframe: this.timeframeBtnTargets }
    const valueMap = { granularity: this.granularity, metric: this.metric, dimension: this.dimension, timeframe: this.timeframe }
    const targets = targetMap[group] || []
    const activeValue = valueMap[group]
    targets.forEach(btn => {
      const isActive = btn.dataset.value === activeValue
      btn.style.background = isActive ? C.bgCard : "transparent"
      btn.style.color = isActive ? C.dark : C.muted
      btn.style.fontWeight = isActive ? "700" : "500"
      btn.style.boxShadow = isActive ? "0 1px 2px rgba(45,48,71,0.08)" : "none"
    })
  }

  render() {
    const m = METRICS[this.metric]
    this.renderDescription(m)
    this.updateTimeframeVisibility()
    this.updateLocationControlsVisibility()
    this.updateLoadMoreVisibility()

    if (this.dimension !== "all") {
      const dimEntry = this.dimensionsValue[this.dimension] || {}
      let dimData
      if (this.timeframe === "all") {
        dimData = dimEntry.all || []
      } else {
        dimData = (dimEntry[this.timeframe] || [])
          .filter(c => this.metricInfo(c).size > 0)
          .sort((a, b) => this.metricInfo(b).size - this.metricInfo(a).size)
      }
      this.renderTable(dimData, m, true)
      this.renderChart(dimData, dimData, m)
    } else {
      const cohorts = this.cohortData[this.granularity] || []
      const sorted = [...cohorts].reverse()
      this.renderTable(sorted, m, false)
      this.renderChart(cohorts, sorted, m)
    }
  }

  // True when the currently displayed "All" cohort data differs from the
  // embedded default window (a location filter is active, or "Load more" has
  // extended the window), and therefore must be fetched from the server.
  needsServerFetch(granularity) {
    return this.activeLocationNames() !== null ||
      this.monthsBack[granularity] !== this.constructor.INITIAL_MONTHS_BACK[granularity]
  }

  // Ensures cohortData for the active granularity reflects the current
  // location filter and window, fetching from the server when needed, then
  // re-renders. Used by granularity changes, location changes, and "Load more".
  refreshCohorts() {
    const g = this.granularity
    if (!this.needsServerFetch(g)) {
      this.cohortData[g] = (this.defaultCohorts[g] || []).slice()
      this.hasMore[g] = !!(this.hasMoreValue || {})[g]
      this.render()
      return
    }

    this.setLoadMoreLoading(true)
    const params = new URLSearchParams()
    params.set("granularity", g)
    params.set("months_back", this.monthsBack[g])
    ;(this.activeLocationNames() || []).forEach(l => params.append("locations[]", l))

    fetch(`${this.cohortsUrlValue}?${params.toString()}`, {
      headers: { Accept: "application/json" },
    })
      .then(r => r.json())
      .then(data => {
        this.cohortData[g] = data.rows || []
        this.hasMore[g] = !!data.has_more
        if (g === this.granularity) this.render()
      })
      .catch(() => {})
      .finally(() => this.setLoadMoreLoading(false))
  }

  loadMore() {
    if (this.loadingCohorts) return
    const g = this.granularity
    this.monthsBack[g] += this.constructor.LOAD_MORE_MONTHS
    this.refreshCohorts()
  }

  updateLoadMoreVisibility() {
    if (!this.hasLoadMoreControlsTarget) return
    const show = this.dimension === "all" && this.hasMore[this.granularity]
    this.loadMoreControlsTarget.style.display = show ? "" : "none"
  }

  setLoadMoreLoading(loading) {
    this.loadingCohorts = loading
    if (this.hasLoadMoreBtnTarget) this.loadMoreBtnTarget.disabled = loading
    if (this.hasLoadMoreLabelTarget) {
      this.loadMoreLabelTarget.textContent = loading
        ? "Loading\u2026"
        : `Load ${this.constructor.LOAD_MORE_MONTHS} more months`
    }
  }

  updateTimeframeVisibility() {
    if (!this.hasTimeframeControlsTarget) return
    this.timeframeControlsTarget.style.display = this.dimension !== "all" ? "" : "none"
  }

  // Full universe of location labels, taken from the all-time rows so the
  // dropdown offers a stable option list regardless of the active time frame.
  allLocationLabels() {
    const entry = this.dimensionsValue.location || {}
    const rows = entry.all || []
    return rows.map(r => r.label).filter(Boolean).sort((a, b) => a.localeCompare(b))
  }

  // The selected location names when a partial filter is active, or null when
  // the selection is empty or covers every location (both mean "all", so no
  // server-side filtering is applied).
  activeLocationNames() {
    const total = this.allLocationLabels().length
    const n = this.selectedLocations.size
    if (n === 0 || n >= total) return null
    return [...this.selectedLocations]
  }

  updateLocationControlsVisibility() {
    if (!this.hasLocationControlsTarget) return
    const show = this.dimension === "all"
    this.locationControlsTarget.style.display = show ? "" : "none"
    if (!show) this.closeLocationPanel()
    this.updateLocationFilterLabel()
  }

  updateLocationFilterLabel() {
    if (!this.hasLocationFilterLabelTarget) return
    const total = this.allLocationLabels().length
    const n = this.selectedLocations.size
    this.locationFilterLabelTarget.textContent =
      (n === 0 || n >= total) ? "All locations" : `${n} location${n === 1 ? "" : "s"} selected`
  }

  toggleLocationFilter() {
    this.locationPanelOpen = !this.locationPanelOpen
    if (this.hasLocationPanelTarget) {
      this.locationPanelTarget.style.display = this.locationPanelOpen ? "" : "none"
    }
    if (this.locationPanelOpen) {
      this.buildLocationOptions()
      if (this.hasLocationSearchTarget) this.locationSearchTarget.focus()
    }
  }

  closeLocationPanel() {
    this.locationPanelOpen = false
    if (this.hasLocationPanelTarget) this.locationPanelTarget.style.display = "none"
  }

  escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  buildLocationOptions() {
    if (!this.hasLocationOptionsTarget) return
    const search = (this.hasLocationSearchTarget ? this.locationSearchTarget.value : "").trim().toLowerCase()
    const labels = this.allLocationLabels().filter(l => l.toLowerCase().includes(search))
    if (labels.length === 0) {
      this.locationOptionsTarget.innerHTML = `<div class="loc-filter-empty">No locations match your search</div>`
      return
    }
    this.locationOptionsTarget.innerHTML = labels.map(label => {
      const esc = this.escapeHtml(label)
      const checked = this.selectedLocations.has(label) ? "checked" : ""
      return `<label class="loc-filter-option"><input type="checkbox" value="${esc}" ${checked} data-action="change->cohort-retention#toggleLocationOption"><span>${esc}</span></label>`
    }).join("")
  }

  filterLocationOptions() {
    this.buildLocationOptions()
  }

  toggleLocationOption(event) {
    const label = event.target.value
    if (event.target.checked) {
      this.selectedLocations.add(label)
    } else {
      this.selectedLocations.delete(label)
    }
    this.updateLocationFilterLabel()
    this.refreshCohorts()
  }

  selectAllLocations() {
    this.allLocationLabels().forEach(l => this.selectedLocations.add(l))
    this.buildLocationOptions()
    this.updateLocationFilterLabel()
    this.refreshCohorts()
  }

  clearLocations() {
    this.selectedLocations.clear()
    this.buildLocationOptions()
    this.updateLocationFilterLabel()
    this.refreshCohorts()
  }

  // Resolves the cohort size and day-value map for the active metric,
  // handling both the all-time row shape ({ size, received: {day: pct} }) and
  // the time-frame row shape ({ received: { size, values: {day: pct} } }).
  metricInfo(c) {
    const md = c[this.metric]
    if (md && typeof md === "object" && "values" in md) {
      return { size: md.size, days: md.values || {} }
    }
    return { size: c.size, days: md || {} }
  }

  renderDescription(m) {
    if (this.hasSectionDescriptionTarget) {
      this.sectionDescriptionTarget.textContent = m.description + "."
    }
    if (this.hasGoalCopyTarget) {
      this.goalCopyTarget.textContent = m.goalCopy + "."
    }
  }

  renderTable(sorted, m, isDimension) {
    if (this.hasTableHeadTarget) {
      this.tableHeadTarget.innerHTML = this.buildFullTableHeader(m, isDimension)
    }

    if (this.hasCohortCountTarget) {
      if (isDimension) {
        const dimLabels = { health_system: "health systems", location: "locations", destination: "destinations" }
        this.cohortCountTarget.textContent = `${sorted.length} ${dimLabels[this.dimension] || "groups"} \u00b7 cells show cumulative % at each day-since-submission`
      } else {
        this.cohortCountTarget.textContent = `${sorted.length} cohorts \u00b7 cells show cumulative % at each day-since-submission`
      }
    }

    let bodyHtml = ""
    sorted.forEach(c => {
      bodyHtml += this.buildTableRow(c, m)
    })
    if (this.hasTableBodyTarget) {
      this.tableBodyTarget.innerHTML = bodyHtml
    }
  }

  buildFullTableHeader(m, isDimension) {
    const thBase = `font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;padding:8px 10px;border-bottom:1px solid ${C.border};vertical-align:bottom;color:${C.muted};`
    const dimHeaders = { health_system: "Health System", location: "Location", destination: "Destination" }
    const firstColLabel = isDimension ? (dimHeaders[this.dimension] || "Dimension") : "Cohort"
    let html = "<tr>"
    html += `<th style="${thBase}text-align:left;min-width:170px;">${firstColLabel}</th>`
    html += `<th style="${thBase}text-align:right;min-width:60px;">Apps</th>`
    html += `<th style="${thBase}text-align:right;min-width:78px;background:${C.bgInput};border-left:1px solid ${C.border};"><div style="font-size:8px;color:${C.muted};margin-bottom:2px;letter-spacing:0.1em;">LATEST</div>Current</th>`
    m.columns.forEach(d => {
      const isGoal = d === m.deadline
      const bgStyle = isGoal ? `background:${C.purpleSoft};border-top-left-radius:6px;border-top-right-radius:6px;` : ""
      const colorStyle = isGoal ? `color:${C.purple};` : `color:${C.muted};`
      const goalLabel = isGoal ? `<div style="font-size:8px;color:${C.purple};margin-bottom:2px;letter-spacing:0.1em;">&#9660; GOAL</div>` : ""
      html += `<th style="${thBase}text-align:right;min-width:64px;${bgStyle}${colorStyle}">${goalLabel}Day ${d}</th>`
    })
    html += `<th style="${thBase}text-align:right;min-width:90px;background:${C.bgInput};border-left:1px solid ${C.border};"><div style="font-size:8px;color:${C.muted};margin-bottom:2px;letter-spacing:0.1em;">@ DAY ${m.deadline}</div>At Deadline</th>`
    html += "</tr>"
    return html
  }

  buildTableRow(c, m) {
    const { size, days: metricData } = this.metricInfo(c)
    const ageDays = c.age_days
    const status = this.statusFor(c, m, metricData)
    const dotColor = status === "hit" ? C.teal : status === "miss" ? C.red : C.amber

    const currentVal = this.valueAtDay(metricData, ageDays, m.columns)
    const currentBg = cellBg(currentVal, m.goal)
    const currentColor = cellTextColor(currentVal, m.goal)
    const currentDisplay = currentVal != null ? `${Math.round(currentVal)}%` : `<span style="color:${C.mutedLight};font-weight:400;">\u2014</span>`

    const partialBadge = c.is_partial
      ? `<span style="font-size:9px;color:${C.amber};background:${C.amberBg};padding:1px 6px;border-radius:3px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;">partial</span>`
      : ""
    const labelStyle = `font-family:${FONT_MONO};font-size:12px;font-weight:700;color:${C.dark};letter-spacing:-0.02em;`
    const escapedLabel = this.escapeHtml(c.label)
    const label = c.url
      ? `<a href="${this.escapeHtml(c.url)}" style="${labelStyle}text-decoration:none;">${escapedLabel}</a>`
      : `<span style="${labelStyle}">${escapedLabel}</span>`

    let html = `<tr data-cohort-id="${c.id}" data-action="mouseenter->cohort-retention#hoverRow mouseleave->cohort-retention#unhoverRow" style="transition:background 0.12s ease;">`
    html += `<td style="text-align:left;padding:6px 10px;border-bottom:1px solid ${C.borderLight};vertical-align:middle;"><div style="display:flex;align-items:center;gap:8px;"><span title="day ${ageDays}" style="width:6px;height:6px;border-radius:50%;background:${dotColor};flex-shrink:0;"></span>${label}${partialBadge}</div></td>`
    html += `<td style="text-align:right;padding:6px 10px;border-bottom:1px solid ${C.borderLight};vertical-align:middle;font-family:${FONT_MONO};font-size:12px;color:${C.body};">${size}</td>`
    html += `<td style="text-align:right;padding:6px 10px;border-bottom:1px solid ${C.borderLight};vertical-align:middle;background:${currentBg};color:${currentColor};font-family:${FONT_MONO};font-size:14px;font-weight:700;letter-spacing:-0.02em;border-left:1px solid ${C.border};transition:background 0.2s ease;">${currentDisplay}</td>`

    m.columns.forEach(d => {
      const v = metricData[String(d)]
      const isGoal = d === m.deadline
      const bg = cellBg(v, m.goal)
      const color = cellTextColor(v, m.goal)
      const borderLeft = isGoal ? `2px solid ${C.purple}` : `1px solid ${C.borderLight}`
      const borderRight = isGoal ? `2px solid ${C.purple}` : `1px solid ${C.borderLight}`
      const display = v != null ? `${Math.round(v)}%` : `<span style="color:${C.mutedLight};font-weight:400;">\u2014</span>`
      html += `<td style="text-align:right;padding:6px 10px;border-bottom:1px solid ${C.borderLight};vertical-align:middle;background:${bg};border-left:${borderLeft};border-right:${borderRight};color:${color};font-family:${FONT_MONO};font-size:12px;font-weight:600;letter-spacing:-0.02em;transition:background 0.2s ease;">${display}</td>`
    })

    const reached = ageDays >= m.deadline
    const dv = reached ? metricData[String(m.deadline)] : null
    if (reached && dv != null) {
      const dlBg = cellBg(dv, m.goal)
      const dlColor = cellTextColor(dv, m.goal)
      html += `<td style="text-align:right;padding:6px 10px;border-bottom:1px solid ${C.borderLight};vertical-align:middle;background:${dlBg};color:${dlColor};font-family:${FONT_MONO};font-size:14px;font-weight:700;letter-spacing:-0.02em;border-left:1px solid ${C.border};transition:background 0.2s ease;">${Math.round(dv)}%</td>`
    } else if (reached) {
      html += `<td style="text-align:right;padding:6px 10px;border-bottom:1px solid ${C.borderLight};vertical-align:middle;background:${C.bgInput};border-left:1px solid ${C.border};"><span style="font-size:11px;font-weight:500;color:${C.mutedLight};">\u2014</span></td>`
    } else {
      const remaining = m.deadline - ageDays
      html += `<td style="text-align:right;padding:6px 10px;border-bottom:1px solid ${C.borderLight};vertical-align:middle;background:${C.bgInput};border-left:1px solid ${C.border};"><span style="font-size:11px;font-weight:500;color:${C.muted};font-family:${FONT_MONO};">${remaining}d to go</span></td>`
    }

    html += "</tr>"
    return html
  }

  valueAtDay(metricData, ageDays, columns) {
    let best = null
    for (const d of columns) {
      if (d <= ageDays && metricData[String(d)] != null) {
        best = metricData[String(d)]
      }
    }
    if (metricData[String(ageDays)] != null) {
      best = metricData[String(ageDays)]
    }
    return best
  }

  statusFor(c, m, metricData) {
    if (c.age_days < m.deadline) return "baking"
    const v = metricData[String(m.deadline)]
    if (v == null) return "baking"
    return v >= m.goal ? "hit" : "miss"
  }

  attachRowHoverListeners() {
    if (!this.hasTableBodyTarget) return
    this.tableBodyTarget.querySelectorAll("tr[data-cohort-id]").forEach(row => {
      row.addEventListener("mouseenter", () => this.highlightCohort(row.dataset.cohortId))
      row.addEventListener("mouseleave", () => this.clearHighlight())
    })
  }

  hoverRow(event) {
    const row = event.currentTarget
    this.highlightCohort(row.dataset.cohortId)
  }

  unhoverRow() {
    this.clearHighlight()
  }

  highlightCohort(cohortId) {
    if (this.hoveredCohort === cohortId) return
    this.hoveredCohort = cohortId
    this.applyTableHighlight(cohortId)
    this.applyChartHighlight(cohortId)
    this.applyLegendHighlight(cohortId)
  }

  clearHighlight() {
    this.hoveredCohort = null
    this.applyTableHighlight(null)
    this.applyChartHighlight(null)
    this.applyLegendHighlight(null)
  }

  applyTableHighlight(cohortId) {
    if (!this.hasTableBodyTarget) return
    this.tableBodyTarget.querySelectorAll("tr[data-cohort-id]").forEach(row => {
      row.style.background = (cohortId && row.dataset.cohortId === cohortId) ? C.purpleSoft : "transparent"
    })
  }

  applyChartHighlight(cohortId) {
    if (!this.chart || !this._chartCohorts) return
    const baseColors = this._chartCohorts.map((_, i) => cohortLineColor(i, this._chartCohorts.length))
    const colors = this._chartCohorts.map((c, i) => {
      if (!cohortId) return baseColors[i]
      if (c.id === cohortId) return baseColors[i]
      return fadeColor(baseColors[i], 0.18)
    })
    const strokeWidths = this._chartCohorts.map(c => {
      if (!cohortId) return 1.5
      return c.id === cohortId ? 2.5 : 1.5
    })
    this.chart.updateOptions({ colors, stroke: { width: strokeWidths, curve: "smooth" } }, false, false)
  }

  applyLegendHighlight(cohortId) {
    if (!this.hasChartLegendTarget) return
    this.chartLegendTarget.querySelectorAll("[data-cohort-id]").forEach(el => {
      if (!cohortId) {
        el.style.color = C.body
        el.style.fontWeight = "500"
      } else if (el.dataset.cohortId === cohortId) {
        el.style.color = C.dark
        el.style.fontWeight = "700"
      } else {
        el.style.color = C.mutedLight
        el.style.fontWeight = "500"
      }
    })
  }

  renderChart(cohorts, sorted, m) {
    const isDimension = this.dimension !== "all"
    const chartCohortLimit = isDimension ? 15 : (this.granularity === "monthly" ? 9 : 8)
    const chartCohorts = sorted.slice(0, Math.min(chartCohortLimit, sorted.length))
    this._chartCohorts = chartCohorts

    const maxDay = 120
    const series = chartCohorts.map((c) => {
      const { days: metricData } = this.metricInfo(c)
      const data = []
      m.columns.forEach(day => {
        if (day <= c.age_days && metricData[String(day)] != null) {
          data.push({ x: day, y: metricData[String(day)] })
        }
      })
      return { name: c.label, data }
    })

    const colors = chartCohorts.map((_, i) => cohortLineColor(i, chartCohorts.length))
    const todayDay = (!isDimension && chartCohorts.length > 0) ? chartCohorts[0].age_days : null

    const options = {
      chart: {
        type: "line",
        height: 360,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
      },
      series,
      colors,
      stroke: {
        width: chartCohorts.map(() => 1.5),
        curve: "smooth",
      },
      markers: { size: 0 },
      xaxis: {
        type: "numeric",
        min: 0,
        max: maxDay,
        tickAmount: 8,
        labels: {
          style: { colors: C.muted, fontSize: "11px", fontFamily: FONT_MONO },
          formatter: v => `${Math.round(v)}`,
        },
        axisBorder: { color: C.border },
        axisTicks: { color: C.border },
        title: {
          text: "days since submission",
          style: { color: C.muted, fontSize: "11px", fontWeight: 400 },
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 4,
        labels: {
          style: { colors: C.muted, fontSize: "11px", fontFamily: FONT_MONO },
          formatter: v => `${v}%`,
        },
        axisBorder: { color: C.border },
        axisTicks: { color: C.border },
      },
      grid: {
        borderColor: C.borderLight,
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
      },
      legend: { show: false },
      tooltip: {
        custom: ({ series: s, seriesIndex, dataPointIndex, w }) => {
          const xVal = w.globals.seriesX[seriesIndex] ? w.globals.seriesX[seriesIndex][dataPointIndex] : null
          const day = xVal != null ? Math.round(xVal) : dataPointIndex
          const items = chartCohorts
            .map((c, i) => {
              const pts = w.globals.seriesX[i] || []
              const idx = pts.findIndex(x => Math.round(x) === day)
              const val = idx >= 0 && s[i] ? s[i][idx] : null
              return { label: c.label, value: val, color: colors[i] }
            })
            .filter(x => x.value != null)
            .sort((a, b) => b.value - a.value)
          if (items.length === 0) return ""
          let html = `<div style="background:${C.dark};color:white;border-radius:8px;padding:10px 14px;font-size:12px;box-shadow:0 8px 24px rgba(45,48,71,0.20);min-width:200px;">`
          html += `<div style="color:${C.mutedLight};font-size:10px;letter-spacing:0.12em;text-transform:uppercase;margin-bottom:8px;font-weight:700;">Day ${day} \u00b7 ${m.label}</div>`
          items.forEach(item => {
            html += `<div style="display:flex;justify-content:space-between;gap:16px;padding:2px 0;font-family:${FONT_MONO};"><span style="display:inline-flex;align-items:center;gap:7px;"><span style="width:8px;height:8px;border-radius:50%;background:${item.color};display:inline-block;"></span><span style="color:rgba(255,255,255,0.82);">${item.label}</span></span><span style="font-weight:700;color:white;">${item.value.toFixed(1)}%</span></div>`
          })
          html += "</div>"
          return html
        },
      },
      annotations: {
        yaxis: [{
          y: m.goal,
          borderColor: C.teal,
          strokeDashArray: 4,
          borderWidth: 1.5,
          label: {
            text: `goal ${m.goal}%`,
            position: "right",
            style: {
              color: C.teal,
              background: "transparent",
              fontSize: "11px",
              fontFamily: FONT_MONO,
              fontWeight: 700,
              padding: { left: 4, right: 4, top: 2, bottom: 2 },
            },
          },
        }],
        xaxis: [
          {
            x: m.deadline,
            borderColor: C.amber,
            strokeDashArray: 4,
            borderWidth: 1.5,
            label: {
              text: `${m.deadlineLabel} (${m.deadline}d)`,
              position: "top",
              orientation: "horizontal",
              style: {
                color: C.amber,
                background: "transparent",
                fontSize: "11px",
                fontWeight: 600,
                padding: { left: 4, right: 4, top: 2, bottom: 2 },
              },
            },
          },
          ...(todayDay != null ? [{
            x: todayDay,
            borderColor: C.red,
            strokeDashArray: 2,
            borderWidth: 1.25,
            label: {
              text: `today (d${todayDay})`,
              position: "top",
              orientation: "horizontal",
              style: {
                color: C.red,
                background: "transparent",
                fontSize: "10px",
                fontFamily: FONT_MONO,
                fontWeight: 700,
                padding: { left: 4, right: 4, top: 2, bottom: 2 },
              },
            },
          }] : []),
        ],
      },
    }

    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }

    if (this.hasChartContainerTarget) {
      this.chart = new ApexCharts(this.chartContainerTarget, options)
      this.chart.render()
    }

    this.renderLegend(chartCohorts, colors, cohorts.length)
  }

  renderLegend(chartCohorts, colors, totalCount) {
    if (!this.hasChartLegendTarget) return
    let html = ""
    chartCohorts.forEach((c, i) => {
      html += `<span data-cohort-id="${c.id}" data-action="mouseenter->cohort-retention#hoverLegend mouseleave->cohort-retention#unhoverLegend" style="display:inline-flex;align-items:center;gap:6px;font-size:11px;color:${C.body};font-family:${FONT_MONO};font-weight:500;cursor:default;transition:color 0.12s;"><span style="width:12px;height:2px;background:${colors[i]};border-radius:1px;display:inline-block;"></span>${c.label}</span>`
    })
    this.chartLegendTarget.innerHTML = html

    if (this.hasChartNoteTarget) {
      if (this.dimension !== "all") {
        const dimLabels = { health_system: "health systems", location: "locations", destination: "destinations" }
        const groupLabel = dimLabels[this.dimension] || "groups"
        this.chartNoteTarget.textContent = chartCohorts.length < totalCount
          ? `Showing ${chartCohorts.length} largest of ${totalCount} ${groupLabel}`
          : `All ${totalCount} ${groupLabel}`
      } else {
        this.chartNoteTarget.textContent = chartCohorts.length < totalCount
          ? `Showing ${chartCohorts.length} most recent of ${totalCount} cohorts`
          : `All ${totalCount} cohorts`
      }
    }
  }

  hoverLegend(event) {
    const cohortId = event.currentTarget.dataset.cohortId
    this.highlightCohort(cohortId)
  }

  unhoverLegend() {
    this.clearHighlight()
  }
}
